/**
 * Free bottle offer - congratulations popup with fireworks.
 *
 * Listens for `free-bottle-offer:unlocked`, which assets/free-bottle-offer.js dispatches the
 * moment free bottles are added to the cart, and shows a congratulations card over a short
 * fireworks animation. It closes after a few seconds, or on the button, a backdrop tap or Esc.
 *
 * Stacking: the cart drawer is a modal <dialog>, which sits in the browser's top layer and
 * makes everything outside it inert. The popup is therefore a manual popover (so it also
 * renders in the top layer, above the drawer) mounted INSIDE the open dialog (so it stays
 * clickable). If the drawer re-renders and drops the node, it is mounted again.
 *
 * Visitors who prefer reduced motion get the card without the fireworks.
 */
(() => {
  const DISPLAY_MS = 5000;
  const LAUNCH_MS = 2600;
  const GRAVITY = 0.1;
  const COLORS = ['#186E90', '#F5A623', '#2EC4B6', '#FF6B6B', '#FFD166', '#FFFFFF'];

  const supportsPopover = typeof HTMLElement.prototype.showPopover === 'function';

  let root = null;
  let hideTimer = 0;
  let stopFireworks = null;
  let shouldBeOpen = false;
  let remountObserver = null;

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const openModalDialog = () => {
    try {
      return document.querySelector('dialog:modal');
    } catch (error) {
      return document.querySelector('dialog[open]');
    }
  };

  const isOpen = () =>
    Boolean(root) && (supportsPopover ? root.matches(':popover-open') : root.classList.contains('is-open'));

  const injectStyles = () => {
    if (document.getElementById('free-bottle-celebration-styles')) return;

    const style = document.createElement('style');
    style.id = 'free-bottle-celebration-styles';
    style.textContent = `
      .fbo-celebration {
        position: fixed;
        inset: 0;
        width: 100%;
        height: 100%;
        max-width: none;
        max-height: none;
        margin: 0;
        padding: 0;
        border: 0;
        background: rgba(23, 31, 38, 0.45);
        color: #171f26;
        display: none;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        z-index: 2147483647;
      }
      .fbo-celebration:popover-open,
      .fbo-celebration.is-open {
        display: flex;
      }
      .fbo-celebration__canvas {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
      }
      .fbo-celebration__card {
        position: relative;
        width: min(88vw, 360px);
        padding: 28px 24px 22px;
        border-radius: 16px;
        background: #ffffff;
        text-align: center;
        box-shadow: 0 24px 60px rgba(0, 0, 0, 0.3);
        font-family: inherit;
        animation: fbo-celebration-pop 0.45s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
      }
      .fbo-celebration__emoji {
        margin: 0 0 10px;
        font-size: 44px;
        line-height: 1;
      }
      .fbo-celebration__title {
        margin: 0 0 6px;
        font-size: 22px;
        font-weight: 700;
        line-height: 1.2;
        color: #171f26;
      }
      .fbo-celebration__text {
        margin: 0 0 4px;
        font-size: 15px;
        color: #3a464f;
      }
      .fbo-celebration__subtext {
        margin: 0 0 18px;
        font-size: 13px;
        color: #6b7a86;
      }
      .fbo-celebration__button {
        width: 100%;
        height: 44px;
        border: 0;
        border-radius: 8px;
        background: #186e90;
        color: #ffffff;
        font: inherit;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
      }
      .fbo-celebration__button:focus-visible {
        outline: 2px solid #171f26;
        outline-offset: 2px;
      }
      @keyframes fbo-celebration-pop {
        from { transform: scale(0.8); opacity: 0; }
        to { transform: scale(1); opacity: 1; }
      }
      @media (prefers-reduced-motion: reduce) {
        .fbo-celebration__card { animation: none; }
      }
    `;
    document.head.appendChild(style);
  };

  const build = () => {
    injectStyles();

    root = document.createElement('div');
    root.className = 'fbo-celebration';
    if (supportsPopover) root.setAttribute('popover', 'manual');
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-labelledby', 'fbo-celebration-title');
    root.innerHTML = `
      <canvas class="fbo-celebration__canvas" aria-hidden="true"></canvas>
      <div class="fbo-celebration__card">
        <p class="fbo-celebration__emoji" aria-hidden="true">&#127881;</p>
        <h2 class="fbo-celebration__title" id="fbo-celebration-title">Congratulations!</h2>
        <p class="fbo-celebration__text" role="status"></p>
        <p class="fbo-celebration__subtext">Added to your cart at $0.00</p>
        <button type="button" class="fbo-celebration__button">Awesome!</button>
      </div>
    `;

    root.addEventListener('click', (event) => {
      if (event.target === root) hide();
    });
    root.querySelector('.fbo-celebration__button').addEventListener('click', hide);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isOpen()) hide();
    });
  };

  // Mount inside the open cart drawer (so the modal dialog doesn't make it inert), else <body>.
  const mount = () => {
    const host = openModalDialog() || document.body;
    if (root.parentElement !== host) {
      if (isOpen() && supportsPopover) root.hidePopover();
      host.appendChild(root);
    }
    if (supportsPopover) {
      if (!root.matches(':popover-open')) root.showPopover();
    } else {
      root.classList.add('is-open');
    }
  };

  const watchForRemoval = () => {
    if (remountObserver) return;
    remountObserver = new MutationObserver(() => {
      if (shouldBeOpen && root && !root.isConnected) mount();
    });
    remountObserver.observe(document.body, { childList: true, subtree: true });
  };

  const hide = () => {
    shouldBeOpen = false;
    window.clearTimeout(hideTimer);
    if (remountObserver) {
      remountObserver.disconnect();
      remountObserver = null;
    }
    if (stopFireworks) {
      stopFireworks();
      stopFireworks = null;
    }
    if (!root) return;
    if (supportsPopover) {
      if (root.matches(':popover-open')) root.hidePopover();
    } else {
      root.classList.remove('is-open');
    }
  };

  const launchFireworks = (canvas) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return () => {};

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = 0;
    let height = 0;
    const resize = () => {
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const rockets = [];
    const sparks = [];
    const pick = (list) => list[Math.floor(Math.random() * list.length)];
    const start = performance.now();
    let last = start;
    let nextLaunch = start;
    let frame = 0;

    const launch = () => {
      const targetY = height * (0.12 + Math.random() * 0.3);
      // Launch speed that carries the rocket to roughly its target height under gravity.
      const speed = Math.sqrt(2 * GRAVITY * (height - targetY));
      rockets.push({
        x: width * (0.15 + Math.random() * 0.7),
        y: height,
        vx: (Math.random() - 0.5) * 1.2,
        vy: -speed,
        targetY,
        color: pick(COLORS),
      });
    };

    const explode = (rocket) => {
      const count = 60 + Math.floor(Math.random() * 30);
      for (let i = 0; i < count; i += 1) {
        const angle = Math.random() * Math.PI * 2;
        const velocity = 1.5 + Math.random() * 3;
        sparks.push({
          x: rocket.x,
          y: rocket.y,
          vx: Math.cos(angle) * velocity,
          vy: Math.sin(angle) * velocity,
          life: 1,
          decay: 0.012 + Math.random() * 0.01,
          color: Math.random() < 0.7 ? rocket.color : pick(COLORS),
        });
      }
    };

    const tick = (now) => {
      const k = Math.min((now - last) / 16.67, 3);
      last = now;
      ctx.clearRect(0, 0, width, height);

      if (now - start < LAUNCH_MS && now >= nextLaunch) {
        launch();
        if (Math.random() < 0.4) launch();
        nextLaunch = now + 250 + Math.random() * 250;
      }

      ctx.lineWidth = 2;
      for (let i = rockets.length - 1; i >= 0; i -= 1) {
        const rocket = rockets[i];
        const fromX = rocket.x;
        const fromY = rocket.y;
        rocket.x += rocket.vx * k;
        rocket.y += rocket.vy * k;
        rocket.vy += GRAVITY * k;

        ctx.globalAlpha = 1;
        ctx.strokeStyle = rocket.color;
        ctx.beginPath();
        ctx.moveTo(fromX, fromY);
        ctx.lineTo(rocket.x, rocket.y);
        ctx.stroke();

        if (rocket.y <= rocket.targetY || rocket.vy >= -0.5) {
          explode(rocket);
          rockets.splice(i, 1);
        }
      }

      const drag = Math.pow(0.985, k);
      for (let i = sparks.length - 1; i >= 0; i -= 1) {
        const spark = sparks[i];
        spark.vx *= drag;
        spark.vy = spark.vy * drag + 0.05 * k;
        spark.x += spark.vx * k;
        spark.y += spark.vy * k;
        spark.life -= spark.decay * k;

        if (spark.life <= 0) {
          sparks.splice(i, 1);
          continue;
        }

        ctx.globalAlpha = spark.life;
        ctx.fillStyle = spark.color;
        ctx.beginPath();
        ctx.arc(spark.x, spark.y, 1.2 + spark.life * 1.8, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      frame = now - start < LAUNCH_MS || rockets.length || sparks.length ? requestAnimationFrame(tick) : 0;
    };

    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
      ctx.clearRect(0, 0, width, height);
    };
  };

  const show = (freeBottles) => {
    if (!root) build();

    root.querySelector('.fbo-celebration__text').textContent = `You've unlocked ${freeBottles} FREE ${
      freeBottles === 1 ? 'bottle' : 'bottles'
    }`;

    shouldBeOpen = true;
    mount();
    watchForRemoval();
    root.querySelector('.fbo-celebration__button').focus({ preventScroll: true });

    if (stopFireworks) stopFireworks();
    stopFireworks = prefersReducedMotion() ? null : launchFireworks(root.querySelector('.fbo-celebration__canvas'));

    window.clearTimeout(hideTimer);
    hideTimer = window.setTimeout(hide, DISPLAY_MS);
  };

  document.addEventListener('free-bottle-offer:unlocked', (event) => {
    const freeBottles = (event.detail && event.detail.freeBottles) || 2;
    show(freeBottles);
  });
})();
